// The Dungeon of Self — a raymarched neon stone corridor you fly through, with
// flickering torches, glowing runes and periodic arches. Backdrop for the
// gate-by-gate narrative overlay.
import type { Scene } from './Engine';
import { HDR, deleteProgram, type Program } from './gl';

const FS = HDR + `
uniform float L;
float sdBox(vec3 p, vec3 b){ vec3 q=abs(p)-b; return length(max(q,0.))+min(max(q.x,max(q.y,q.z)),0.); }
float hash1(float n){ return fract(sin(n)*43758.5453); }
vec2 map(vec3 p){
  float corridor = sdBox(vec3(p.x, p.y, 0.0), vec3(2.4, 2.2, 1e4)); // infinite void tube
  float d = -corridor; float mat = 1.0;
  vec3 pr = vec3(p.x, p.y, mod(p.z, 8.0) - 4.0);
  float ring = max(sdBox(pr, vec3(3.4, 3.0, 0.6)), -sdBox(pr, vec3(1.7, 2.0, 2.0)));
  if (ring < d) { d = ring; mat = 2.0; }
  return vec2(d, mat);
}
vec3 calcN(vec3 p){ vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(map(p+e.xyy).x-map(p-e.xyy).x, map(p+e.yxy).x-map(p-e.yxy).x, map(p+e.yyx).x-map(p-e.yyx).x)); }
void main(){
  vec2 uv = (gl_FragCoord.xy - 0.5*R)/R.y;
  vec3 ro = vec3(sin(L*0.3)*0.5, sin(L*0.2)*0.3, L*4.0);
  vec3 ta = ro + vec3(sin(L*0.25)*0.3, 0.0, 1.0);
  vec3 ww = normalize(ta-ro), uu = normalize(cross(vec3(0,1,0), ww)), vv = cross(ww, uu);
  vec3 rd = normalize(uv.x*uu + uv.y*vv + 1.4*ww);
  float t = 0.0, mat = 1.0; bool hit=false;
  for (int i=0;i<80;i++){ vec3 pos=ro+rd*t; vec2 h=map(pos); if(h.x<0.002*t){mat=h.y;hit=true;break;} t+=h.x*0.8; if(t>60.) break; }
  vec3 col = vec3(0.02,0.02,0.035);
  if (hit){
    vec3 pos = ro+rd*t; vec3 n = calcN(pos);
    vec3 base = mix(vec3(.30,.28,.32), vec3(.16,.14,.17), hash1(floor(pos.z*2.)+floor(pos.x*3.)));
    if (mat>1.5) base = vec3(.42,.34,.26);
    vec3 lcol = vec3(0.0);
    for (float k=0.0;k<2.0;k++){
      vec3 lp = vec3(mix(-2.2,2.2,k), 1.2, floor(pos.z/8.)*8.+4.);
      float dl = length(lp-pos);
      float fl = 0.7+0.3*sin(T*9.0+k*3.0+floor(pos.z/8.));
      float dif = max(dot(n, normalize(lp-pos)),0.0);
      lcol += vec3(1.0,.55,.2)*dif*fl*3.2/(1.0+dl*dl*0.4);
    }
    float rune = step(0.86, hash1(floor(pos.z*1.5)+floor(pos.y*3.0))) * step(2.0, abs(pos.x));
    vec3 runeCol = vec3(.2,.9,1.0)*rune*(0.4+0.7*K);
    col = base*(0.12+lcol) + runeCol;
    col = mix(col, vec3(0.03,0.02,0.05), 1.0-exp(-t*0.06));
  }
  O = vec4(col, 1.0);
}`;

export default function dungeon(): Scene {
  let p: Program; let l = 0;
  return {
    scale: .6, bloom: .95, crt: .55,
    init(e) { p = e.prog(FS); },
    frame(f) { l += f.dt * (2.2 + f.key * 1.5); f.e.u(p).f('L', l); f.e.fullscreen(p, f); },
    dispose(e) { deleteProgram(e.gl, p); },
  };
}
